import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import Switch from '@material-ui/core/Switch';
import { createStructuredSelector } from 'reselect';
import { withStyles } from '@material-ui/core/styles';

import { Link } from 'react-router-dom';
import styles from './styles';
import DataTable from '../DataTable';
import {
  selectUserAttributes,
  selectEmailNotificationSettingsUpdatingStatus,
  selectSMSNotificationSettingsUpdatingStatus,
} from '../../store/selectors';
import { updateNotificationSettings } from '../../store/actions/settings';
import { setUserProfileCustomAttribute } from '../../store/actions/userProfile';
import { isEmptyString } from '../../utilities/helpers';

export const NOTIFICATION_STATUSES = {
  ENABLED: 'Enabled',
  DISABLED: 'Disabled',
};

const propTypes = {
  classes: PropTypes.object.isRequired,
  userAttributes: PropTypes.shape({
    'custom:emailNotiStatus':
      PropTypes.oneOf[
        (NOTIFICATION_STATUSES.ENABLED, NOTIFICATION_STATUSES.DISABLED)
      ],
    'custom:smsNotiStatus':
      PropTypes.oneOf[
        (NOTIFICATION_STATUSES.ENABLED, NOTIFICATION_STATUSES.DISABLED)
      ],
  }).isRequired,
  onUpdateNotificationSettings: PropTypes.func.isRequired,
  onSetUserProfileCustomAttribute: PropTypes.func.isRequired,
  emailNotificationSettingsUpdatingStatus: PropTypes.bool.isRequired,
  SMSNotificationSettingsUpdatingStatus: PropTypes.bool.isRequired,
};

export const NOTIFICATION_TYPES = {
  EMAIL: 'email',
  SMS: 'SMS',
};

export const getCustomAttributeKeyForNotificationType = notificationType => {
  switch (notificationType) {
    case NOTIFICATION_TYPES.EMAIL:
      return 'custom:emailNotiStatus';
    case NOTIFICATION_TYPES.SMS:
      return 'custom:smsNotiStatus';
    default:
      // eslint-disable-next-line no-console
      console.warn('Reach unknown notification type');
      return 'invalidKey';
  }
};

export const getNextNofificationStatus = currentStatus => {
  if (currentStatus === NOTIFICATION_STATUSES.ENABLED) {
    return NOTIFICATION_STATUSES.DISABLED;
  }
  return NOTIFICATION_STATUSES.ENABLED;
};

export const NotificationSettingsContainer = props => {
  const {
    classes,
    userAttributes,
    onUpdateNotificationSettings,
    onSetUserProfileCustomAttribute,
    emailNotificationSettingsUpdatingStatus,
    SMSNotificationSettingsUpdatingStatus,
  } = props;

  useEffect(() => {
    // If the builder enables SMS notification before, but then later on he removes his mobile number from his profile,
    // we need to turn off SMS notification if it has not been turned off. This acts as a second layer of safe guard.
    // The first layer of safe guard needs to be put in place where we update the user profile, if the mobile number field is empty,
    // we set custom:mobileNumber to be 'Disabled'.
    if (
      !builderHasMobileNumber() &&
      !SMSNotificationSettingsUpdatingStatus &&
      userAttributes['custom:smsNotiStatus'] === NOTIFICATION_STATUSES.ENABLED
    ) {
      onSetUserProfileCustomAttribute(
        'custom:smsNotiStatus',
        NOTIFICATION_STATUSES.DISABLED,
      );

      onUpdateNotificationSettings(
        localStorage.getItem('accessToken'),
        'custom:smsNotiStatus',
        NOTIFICATION_TYPES.SMS,
        NOTIFICATION_STATUSES.DISABLED,
      );
    }
  });

  const columns = [
    {
      index: 0,
      title: 'Notification',
      name: 'notificationFullName',
      sort: {
        enabled: false,
      },
    },
    {
      index: 1,
      title: 'Status',
      name: 'status',
      sort: {
        enabled: false,
      },
      cell: row => (
        <Switch
          checked={checkiFNotificationEnabled(row.notificationType)}
          onChange={() => handleStatusChange(row.notificationType)}
          value={`${row.notificationType} notifications enabled`}
          disabled={shouldDislableSwitchToggle(row.notificationType)}
        />
      ),
    },
    {
      index: 2,
      title: 'Description',
      name: 'description',
      sort: {
        enabled: false,
      },
      cell: row => {
        if (row.notificationType === NOTIFICATION_TYPES.SMS) {
          if (!builderHasMobileNumber()) {
            return row.descriptionAlternative;
          }
        }
        // eslint-disable-next-line react/no-danger
        return row.description;
      },
    },
  ];

  const data = [
    {
      notificationType: NOTIFICATION_TYPES.EMAIL,
      notificationFullName: 'Email notifications',
      description: (
        <div>
          Email notification will be sent to{' '}
          <strong>{userAttributes['custom:personalEmail']}</strong> when a new
          job enquiry is received.
        </div>
      ),
    },
    {
      notificationType: NOTIFICATION_TYPES.SMS,
      notificationFullName: 'SMS notifications',
      description: (
        <div>
          SMS notification will be sent to{' '}
          <strong>{userAttributes['custom:mobileNumber']}</strong> when a new
          job enquiry is received.
        </div>
      ),
      descriptionAlternative: (
        <div>
          To enable SMS notifications please provide your mobile number in{' '}
          <Link to="/my-build-profile/builder-profile">myHardies Profile</Link>
        </div>
      ),
    },
  ];

  const shouldDislableSwitchToggle = notificationType => {
    switch (notificationType) {
      case NOTIFICATION_TYPES.EMAIL:
        return emailNotificationSettingsUpdatingStatus;
      case NOTIFICATION_TYPES.SMS: {
        if (!builderHasMobileNumber()) return true;
        return SMSNotificationSettingsUpdatingStatus;
      }
      default:
        return true;
    }
  };

  const builderHasMobileNumber = () => {
    const mobileNumber = userAttributes['custom:mobileNumber'];

    return mobileNumber !== undefined && !isEmptyString(mobileNumber);
  };

  const checkiFNotificationEnabled = notificationType => {
    let notificationStatus = null;

    switch (notificationType) {
      case NOTIFICATION_TYPES.EMAIL:
        notificationStatus = userAttributes['custom:emailNotiStatus'];
        break;
      case NOTIFICATION_TYPES.SMS:
        notificationStatus = userAttributes['custom:smsNotiStatus'];
        break;
      default:
        // eslint-disable-next-line no-console
        console.warn('Reach unknown notification type');
    }

    switch (notificationStatus) {
      case NOTIFICATION_STATUSES.ENABLED:
        return true;
      case NOTIFICATION_STATUSES.DISABLED:
        return false;
      default:
        // eslint-disable-next-line no-console
        console.warn('Reach unknown notification status');
        return false;
    }
  };

  const handleStatusChange = notificationType => {
    const customAttributeKey = getCustomAttributeKeyForNotificationType(
      notificationType,
    );

    const curretStatus = checkiFNotificationEnabled(notificationType)
      ? NOTIFICATION_STATUSES.ENABLED
      : NOTIFICATION_STATUSES.DISABLED;

    const nextNofificationStatus = getNextNofificationStatus(curretStatus);

    onSetUserProfileCustomAttribute(customAttributeKey, nextNofificationStatus);

    onUpdateNotificationSettings(
      localStorage.getItem('accessToken'),
      customAttributeKey,
      notificationType,
      nextNofificationStatus,
    );
  };

  return (
    <div className={classes.root}>
      <div className={classes.innerWrapper}>
        <div className={classes.introText}>
          Please select how you want to receive notifications below. You can
          update these settings at any time.
        </div>
        <DataTable columns={columns} data={data} sticky={false} />
      </div>
    </div>
  );
};

NotificationSettingsContainer.propTypes = propTypes;

const mapStateToProps = createStructuredSelector({
  userAttributes: selectUserAttributes(),
  emailNotificationSettingsUpdatingStatus: selectEmailNotificationSettingsUpdatingStatus(),
  SMSNotificationSettingsUpdatingStatus: selectSMSNotificationSettingsUpdatingStatus(),
});

const mapDispatchToProps = dispatch => ({
  onUpdateNotificationSettings: (
    accessToken,
    customAttributeKey,
    notificationType,
    value,
  ) =>
    dispatch(
      updateNotificationSettings(
        accessToken,
        customAttributeKey,
        notificationType,
        value,
      ),
    ),
  onSetUserProfileCustomAttribute: (key, value) =>
    dispatch(setUserProfileCustomAttribute(key, value)),
});

export default withStyles(styles)(
  connect(
    mapStateToProps,
    mapDispatchToProps,
  )(NotificationSettingsContainer),
);
